import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, CheckCircle2, ClipboardCheck, ChevronRight, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const TYPE_LABELS = { mcq: "Multiple choice", multi: "Multiple select", tf: "True / false" };

function newOption(text = "", correct = false) {
  return { id: `tmp-${Math.random().toString(36).slice(2)}`, text, correct };
}
function newQuestion(type = "mcq") {
  const base = { id: `tmp-${Math.random().toString(36).slice(2)}`, type, text: "" };
  if (type === "tf") base.options = [newOption("True", true), newOption("False")];
  else base.options = [newOption(), newOption()];
  return base;
}

export default function AdminAssessments() {
  const [courses, setCourses] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [passMark, setPassMark] = useState(80);
  const [maxRetakes, setMaxRetakes] = useState(3);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/courses").then(({ data }) => {
      setCourses(data);
      if (data[0]) selectCourse(data[0].id);
    });
  }, []);

  const selectCourse = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/admin/courses/${id}/assessment`);
    const a = data.assessment;
    setPassMark(a?.pass_mark ?? data.course.pass_mark ?? 80);
    setMaxRetakes(a?.max_retakes ?? 3);
    setQuestions(a?.questions?.length ? a.questions : [newQuestion()]);
  };

  const setQ = (qid, patch) => setQuestions((qs) => qs.map((q) => (q.id === qid ? { ...q, ...patch } : q)));
  const setOpt = (qid, oid, patch) =>
    setQuestions((qs) => qs.map((q) => q.id === qid
      ? { ...q, options: q.options.map((o) => (o.id === oid ? { ...o, ...patch } : o)) } : q));

  const toggleCorrect = (q, oid) => {
    if (q.type === "multi") {
      setOpt(q.id, oid, { correct: !q.options.find((o) => o.id === oid).correct });
    } else {
      setQuestions((qs) => qs.map((x) => x.id === q.id
        ? { ...x, options: x.options.map((o) => ({ ...o, correct: o.id === oid })) } : x));
    }
  };

  const changeType = (qid, type) => setQuestions((qs) => qs.map((q) => q.id === qid ? newQuestion(type) && { ...newQuestion(type), id: q.id, text: q.text } : q));

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        title: "Final assessment",
        pass_mark: Number(passMark),
        max_retakes: Number(maxRetakes),
        questions: questions.map((q) => ({
          id: q.id.startsWith("tmp-") ? null : q.id,
          type: q.type,
          text: q.text,
          options: q.options.map((o) => ({ id: o.id.startsWith("tmp-") ? null : o.id, text: o.text, correct: !!o.correct })),
        })),
      };
      await api.put(`/admin/courses/${activeId}/assessment`, payload);
      toast.success("Assessment saved");
      selectCourse(activeId);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
      {/* course list */}
      <aside className="card-surface h-max overflow-hidden">
        <div className="border-b border-border bg-secondary/50 px-4 py-2.5 text-[13px] font-semibold">Courses</div>
        <div className="divide-y divide-border">
          {courses.map((c) => (
            <button key={c.id} onClick={() => selectCourse(c.id)} data-testid={`assessment-course-${c.id}`}
              className={cn("flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm hover:bg-accent",
                activeId === c.id && "bg-[#33B6E4]/10")}>
              <span className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-muted-foreground" /> {c.title}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      </aside>

      {/* editor */}
      <div className="space-y-4">
        <div className="card-surface flex flex-wrap items-end gap-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Pass mark (%)</label>
            <input type="number" min="1" max="100" value={passMark} onChange={(e) => setPassMark(e.target.value)}
              data-testid="pass-mark-input" className="h-10 w-28 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Retakes allowed</label>
            <input type="number" min="0" max="10" value={maxRetakes} onChange={(e) => setMaxRetakes(e.target.value)}
              data-testid="retakes-input" className="h-10 w-28 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={() => setQuestions((q) => [...q, newQuestion()])} data-testid="add-question-button">
              <Plus className="mr-1.5 h-4 w-4" /> Add question
            </Button>
            <Button className="btn-brand" onClick={save} disabled={loading} data-testid="save-assessment-button">
              <Save className="mr-1.5 h-4 w-4" /> {loading ? "Saving…" : "Save assessment"}
            </Button>
          </div>
        </div>

        {questions.map((q, qi) => (
          <div key={q.id} className="card-surface p-5" data-testid={`edit-question-${qi}`}>
            <div className="flex items-center gap-3">
              <span className="font-heading text-sm font-semibold text-muted-foreground">Q{qi + 1}</span>
              <Select value={q.type} onValueChange={(v) => changeType(q.id, v)}>
                <SelectTrigger className="h-9 w-[170px]" data-testid={`question-type-${qi}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <button className="ml-auto text-muted-foreground hover:text-destructive" onClick={() => setQuestions((qs) => qs.filter((x) => x.id !== q.id))} data-testid={`delete-question-${qi}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <input value={q.text} onChange={(e) => setQ(q.id, { text: e.target.value })} placeholder="Question text"
              data-testid={`question-text-${qi}`}
              className="mt-3 h-10 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring" />

            <div className="mt-3 space-y-2">
              {q.options.map((o) => (
                <div key={o.id} className="flex items-center gap-2">
                  <button type="button" onClick={() => toggleCorrect(q, o.id)} data-testid={`mark-correct-${o.id}`}
                    className={cn("grid h-6 w-6 shrink-0 place-items-center rounded-full border",
                      o.correct ? "border-emerald-500 bg-emerald-500 text-white" : "border-muted-foreground/40 text-transparent")}
                    title="Mark as correct">
                    <CheckCircle2 className="h-4 w-4" />
                  </button>
                  <input value={o.text} onChange={(e) => setOpt(q.id, o.id, { text: e.target.value })}
                    disabled={q.type === "tf"} placeholder="Option text"
                    className="h-9 flex-1 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-70" />
                  {q.type !== "tf" && q.options.length > 2 && (
                    <button className="text-muted-foreground hover:text-destructive" onClick={() => setQ(q.id, { options: q.options.filter((x) => x.id !== o.id) })}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              {q.type !== "tf" && (
                <button className="text-xs font-medium text-[#1779AC] hover:underline dark:text-[#5cc7ef]"
                  onClick={() => setQ(q.id, { options: [...q.options, newOption()] })} data-testid={`add-option-${qi}`}>
                  + Add option
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
